import type { ProductoPos } from './tipos'

/**
 * Qué productos coinciden con lo que hay tipeado en el buscador del POS.
 *
 * Vive en su propio archivo y no adentro de la grilla porque la pregunta se
 * hace desde los DOS lados: la grilla la usa para mostrar, y la pantalla la usa
 * para decidir qué agrega el Enter cuando lo tipeado no es un código ni una
 * etiqueta de balanza. Escrita dos veces, el Enter podría agregar un producto
 * distinto del único que el cajero está viendo, que es la clase de diferencia
 * que se descubre con el cliente adelante.
 *
 * Busca por nombre y por CUALQUIERA de los códigos: el cajero con práctica
 * tipea el código corto o el PLU, el nuevo escribe el nombre, y el lector
 * dispara el código de barras. Un solo campo para los cuatro.
 */
export function coincidencias(productos: ProductoPos[], texto: string): ProductoPos[] {
  const q = normalizar(texto.trim())
  if (!q) return productos

  return productos.filter(
    (p) =>
      normalizar(p.nombre).includes(q) ||
      // Los códigos se normalizan TAMBIÉN, y no es cosmético: el código corto
      // que genera el sistema es «P0001» y la consulta va en minúsculas, así
      // que tipear el código —que es como busca el cajero con práctica— no
      // encontraba nunca nada. Andaba de casualidad con el código de barras y
      // el PLU, que son solo dígitos.
      normalizar(p.codigoCorto ?? '').includes(q) ||
      normalizar(p.codigoBarras ?? '').includes(q) ||
      String(p.pluBalanza ?? '').includes(q),
  )
}

/**
 * Los productos que la grilla está MOSTRANDO, en el mismo orden.
 *
 * Es la lista contra la que se mueven las flechas y de la que sale el producto
 * que agrega el Enter, así que no puede calcularla la grilla por su cuenta: el
 * índice de la marca apuntaría a una lista y el Enter a otra, que es la forma
 * exacta de agregar un producto distinto del que está resaltado en pantalla.
 * Es el mismo motivo por el que `coincidencias` salió del componente.
 *
 * Buscar es GLOBAL: con texto tipeado el chip de categoría no filtra. Si
 * siguiera filtrando, escribir «banana» con «Hortaliza» apretado diría que no
 * hay nada teniendo la banana en el catálogo, y nadie se acuerda de qué chip
 * dejó apretado.
 */
export function productosVisibles(
  productos: ProductoPos[],
  filtro: string,
  categoria: number | null,
): ProductoPos[] {
  const texto = filtro.trim()

  if (texto) return coincidencias(productos, texto)
  if (categoria === null) return productos

  return productos.filter((p) => p.categoriaId === categoria)
}

/** Minúsculas y sin acentos: nadie tipea «limón» con tilde apurado. */
export function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}
